import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { projectsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { AppDrawer } from '../ui/AppDrawer';
import { Plus, Loader2, Trash2, GripVertical, Calendar, ChevronLeft, ChevronRight, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CFG = {
  not_started:  { label: 'Not Started',  color: 'bg-slate-200 dark:bg-slate-700', bar: 'bg-slate-400' },
  in_progress:  { label: 'In Progress',  color: 'bg-blue-100 dark:bg-blue-900/40', bar: 'bg-blue-500' },
  completed:    { label: 'Completed',     color: 'bg-green-100 dark:bg-green-900/40', bar: 'bg-green-500' },
};

const DEFAULT_COLORS = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#06B6D4','#84CC16'];

function formatDate(d) {
  if (!d) return '';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function toIso(d) { return d ? new Date(d + 'T00:00:00').toISOString().split('T')[0] : ''; }
function addDays(isoDate, days) {
  const d = new Date(isoDate + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

export function ProjectSchedule({ projectId, canEdit, teamUsers }) {
  const [items, setItems] = useState([]);
  const [timeline, setTimeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const ganttRef = useRef(null);
  const [dragInfo, setDragInfo] = useState(null); // { id, startX, origStart, origEnd, duration }

  const fetchSchedule = useCallback(async () => {
    try {
      const res = await projectsAPI.listSchedule(projectId);
      setItems(res.data.items || []);
      setTimeline(res.data.timeline || {});
    } catch { toast.error('Failed to load schedule'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  // Compute Gantt range
  const { ganttStart, totalDays, colWidth, dayToX } = useMemo(() => {
    if (!items.length) return { ganttStart: null, totalDays: 0, colWidth: 32, dayToX: () => 0 };
    let earliest = null, latest = null;
    for (const i of items) {
      if (i.start_date) {
        const s = new Date(i.start_date + 'T00:00:00');
        if (!earliest || s < earliest) earliest = s;
      }
      if (i.end_date) {
        const e = new Date(i.end_date + 'T00:00:00');
        if (!latest || e > latest) latest = e;
      }
    }
    if (!earliest) earliest = new Date();
    if (!latest) latest = new Date(earliest.getTime() + 30 * 86400000);
    // Pad
    earliest = new Date(earliest.getTime() - 3 * 86400000);
    latest = new Date(latest.getTime() + 7 * 86400000);
    const days = Math.max(Math.ceil((latest - earliest) / 86400000), 14);
    const cw = 32;
    return {
      ganttStart: earliest,
      totalDays: days,
      colWidth: cw,
      dayToX: (dateStr) => {
        const d = new Date(dateStr + 'T00:00:00');
        return Math.round((d - earliest) / 86400000) * cw;
      },
    };
  }, [items]);

  // Generate week headers
  const weekHeaders = useMemo(() => {
    if (!ganttStart) return [];
    const headers = [];
    const d = new Date(ganttStart);
    d.setDate(d.getDate() - d.getDay()); // align to Sunday
    while (d <= new Date(ganttStart.getTime() + totalDays * 86400000)) {
      headers.push({ date: new Date(d), label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) });
      d.setDate(d.getDate() + 7);
    }
    return headers;
  }, [ganttStart, totalDays]);

  // Today line position
  const todayX = useMemo(() => {
    if (!ganttStart) return null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayDiff = (today - ganttStart) / 86400000;
    if (dayDiff < 0 || dayDiff > totalDays) return null;
    return dayDiff * colWidth + colWidth * (now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()) / 86400;
  }, [ganttStart, totalDays, colWidth]);

  // Drawer form state
  const [form, setForm] = useState({});
  const openDrawer = (item = null) => {
    setEditItem(item);
    setForm(item ? {
      name: item.name, description: item.description || '', start_date: item.start_date || '',
      end_date: item.end_date || '', duration_days: item.duration_days || '',
      assigned_to: item.assigned_to || '', status: item.status || 'not_started',
      color: item.color || DEFAULT_COLORS[0], depends_on_id: item.depends_on_id || '',
      percent_complete: item.percent_complete || 0,
      progress_override: item.progress_override || false,
      auto_percent: item.auto_percent,
      task_count: item.task_count || 0,
      task_done_count: item.task_done_count || 0,
    } : {
      name: '', description: '', start_date: '', end_date: '', duration_days: '',
      assigned_to: '', status: 'not_started', color: DEFAULT_COLORS[items.length % DEFAULT_COLORS.length],
      depends_on_id: '', percent_complete: 0, progress_override: false,
      auto_percent: null, task_count: 0, task_done_count: 0,
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        duration_days: form.duration_days ? parseInt(form.duration_days) : undefined,
        depends_on_id: form.depends_on_id || null,
        percent_complete: form.progress_override ? (form.percent_complete || 0) : undefined,
        progress_override: form.progress_override,
      };
      if (editItem) {
        const res = await projectsAPI.updateScheduleItem(projectId, editItem.id, payload);
        if (res.data.deps_shifted > 0) {
          toast.info(`${res.data.deps_shifted} dependent phase(s) cascaded`);
        }
      } else {
        await projectsAPI.createScheduleItem(projectId, payload);
      }
      setDrawerOpen(false);
      fetchSchedule();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await projectsAPI.deleteScheduleItem(projectId, editItem.id);
      setDrawerOpen(false);
      fetchSchedule();
      toast.success('Phase deleted');
    } catch { toast.error('Failed to delete'); }
    finally { setSaving(false); }
  };

  // Drag-to-reschedule handlers
  const handleDragStart = (e, item) => {
    if (!canEdit || !item.start_date || !item.end_date) return;
    e.preventDefault();
    const startX = e.clientX;
    const duration = item.duration_days || Math.ceil((new Date(item.end_date + 'T00:00:00') - new Date(item.start_date + 'T00:00:00')) / 86400000);
    setDragInfo({ id: item.id, startX, origStart: item.start_date, origEnd: item.end_date, duration });
  };

  useEffect(() => {
    if (!dragInfo) return;
    const handleMove = (e) => {
      const dx = e.clientX - dragInfo.startX;
      const daysDelta = Math.round(dx / colWidth);
      if (daysDelta === 0) return;
      setItems(prev => prev.map(it => {
        if (it.id !== dragInfo.id) return it;
        return {
          ...it,
          start_date: addDays(dragInfo.origStart, daysDelta),
          end_date: addDays(dragInfo.origEnd, daysDelta),
          _dragging: true,
        };
      }));
    };
    const handleUp = async (e) => {
      const dx = e.clientX - dragInfo.startX;
      const daysDelta = Math.round(dx / colWidth);
      setDragInfo(null);

      if (daysDelta === 0) {
        setItems(prev => prev.map(it => ({ ...it, _dragging: false })));
        return;
      }

      const newStart = addDays(dragInfo.origStart, daysDelta);
      const newEnd = addDays(dragInfo.origEnd, daysDelta);

      try {
        const res = await projectsAPI.updateScheduleItem(projectId, dragInfo.id, {
          start_date: newStart, end_date: newEnd,
        });
        if (res.data.deps_shifted > 0) {
          toast.info(`${res.data.deps_shifted} dependent phase(s) cascaded`);
        }
        fetchSchedule();
      } catch {
        toast.error('Failed to move phase');
        fetchSchedule();
      }
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragInfo, colWidth, projectId, fetchSchedule]);

  // Build dependency index for arrows
  const depPairs = useMemo(() => {
    return items
      .filter(i => i.depends_on_id && i.start_date)
      .map(i => ({ from: i.depends_on_id, to: i.id }))
      .filter(p => items.some(it => it.id === p.from && it.end_date));
  }, [items]);

  // Dependency selectable items (exclude self and items that depend on the current one)
  const depOptions = useMemo(() => {
    if (!editItem) return items;
    return items.filter(i => i.id !== editItem.id);
  }, [items, editItem]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const ganttWidth = totalDays * colWidth;
  const ROW_H = 36;
  const HEADER_H = 44;

  return (
    <div className="space-y-4" data-testid="project-schedule">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {items.length} phase{items.length !== 1 ? 's' : ''}
          {timeline.min_date && ` · ${formatDate(timeline.min_date)} — ${formatDate(timeline.max_date)}`}
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => openDrawer()} data-testid="add-phase-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Phase
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="border rounded-lg p-10 text-center text-muted-foreground text-sm" data-testid="empty-schedule">
          No schedule phases yet. Add a phase to get started.
        </div>
      ) : (
        /* Gantt Chart */
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto" ref={ganttRef}>
            <div className="relative" style={{ minWidth: ganttWidth + 220 }}>
              {/* Phase labels column + Gantt area */}
              <div className="flex">
                {/* Left: Phase names */}
                <div className="flex-shrink-0 w-[220px] border-r bg-muted/20 z-10 sticky left-0">
                  <div className="h-[44px] border-b px-3 flex items-center text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Phase
                  </div>
                  {items.map((item) => {
                    const statusCfg = STATUS_CFG[item.status] || STATUS_CFG.not_started;
                    const dep = item.depends_on_id ? items.find(i => i.id === item.depends_on_id) : null;
                    return (
                      <div
                        key={item.id}
                        className="h-[36px] flex items-center gap-2 px-3 border-b text-sm cursor-pointer hover:bg-muted/30"
                        onClick={() => openDrawer(item)}
                        data-testid={`phase-label-${item.id}`}
                      >
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color || '#94a3b8' }} />
                        <span className="truncate flex-1 font-medium">{item.name}</span>
                        {dep && <Link2 className="w-3 h-3 text-muted-foreground flex-shrink-0" title={`Depends on: ${dep.name}`} />}
                        {item.percent_complete > 0 && item.percent_complete < 100 && (
                          <span className="text-[10px] text-muted-foreground tabular-nums">{item.percent_complete}%</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Right: Gantt bars */}
                <div className="relative flex-1" style={{ minWidth: ganttWidth }}>
                  {/* Week headers */}
                  <div className="h-[44px] border-b flex relative bg-muted/10">
                    {weekHeaders.map((w, i) => (
                      <div key={i} className="absolute top-0 h-full flex items-center text-[10px] text-muted-foreground font-medium pl-1 border-l border-dashed border-muted-foreground/15"
                        style={{ left: Math.round((w.date - ganttStart) / 86400000) * colWidth }}>
                        {w.label}
                      </div>
                    ))}
                  </div>

                  {/* Row backgrounds + bars */}
                  <svg width={ganttWidth} height={items.length * ROW_H} className="block">
                    {/* Row dividers */}
                    {items.map((_, idx) => (
                      <line key={`div-${idx}`} x1={0} y1={(idx + 1) * ROW_H} x2={ganttWidth} y2={(idx + 1) * ROW_H} stroke="currentColor" className="text-border" strokeWidth={0.5} />
                    ))}

                    {/* Weekend shading */}
                    {Array.from({ length: totalDays }, (_, d) => {
                      const date = new Date(ganttStart.getTime() + d * 86400000);
                      if (date.getDay() === 0 || date.getDay() === 6) {
                        return <rect key={`we-${d}`} x={d * colWidth} y={0} width={colWidth} height={items.length * ROW_H} fill="currentColor" className="text-muted-foreground/5" />;
                      }
                      return null;
                    })}

                    {/* Dependency arrows */}
                    {depPairs.map(({ from, to }) => {
                      const fromItem = items.find(i => i.id === from);
                      const toItem = items.find(i => i.id === to);
                      if (!fromItem?.end_date || !toItem?.start_date) return null;

                      const fromIdx = items.indexOf(fromItem);
                      const toIdx = items.indexOf(toItem);
                      const fromX = dayToX(fromItem.end_date) + colWidth;
                      const fromY = fromIdx * ROW_H + ROW_H / 2;
                      const toX = dayToX(toItem.start_date);
                      const toY = toIdx * ROW_H + ROW_H / 2;

                      // Route: horizontal from end of predecessor, down/up, horizontal to start of dependent
                      const midX = Math.max(fromX + 8, toX - 12);
                      const path = fromIdx === toIdx
                        ? `M${fromX},${fromY} H${toX}`
                        : `M${fromX},${fromY} H${midX} V${toY} H${toX}`;

                      return (
                        <g key={`dep-${from}-${to}`}>
                          <path d={path} fill="none" stroke="#64748b" strokeWidth={2} opacity={0.5} />
                          <polygon
                            points={`${toX},${toY} ${toX - 6},${toY - 4} ${toX - 6},${toY + 4}`}
                            fill="#64748b" opacity={0.7}
                          />
                        </g>
                      );
                    })}

                    {/* Bars */}
                    {items.map((item, idx) => {
                      if (!item.start_date || !item.end_date) return null;
                      const x = dayToX(item.start_date);
                      const w = dayToX(item.end_date) - x + colWidth;
                      const y = idx * ROW_H + 6;
                      const h = ROW_H - 12;
                      const isDragging = item._dragging;
                      const pct = item.percent_complete || 0;

                      return (
                        <g key={`bar-${item.id}`} data-testid={`gantt-bar-${item.id}`}>
                          {/* Background bar */}
                          <rect
                            x={x} y={y} width={Math.max(w, colWidth)} height={h} rx={4}
                            fill={item.color || '#94a3b8'} opacity={isDragging ? 0.5 : 0.25}
                            stroke={isDragging ? item.color || '#94a3b8' : 'none'}
                            strokeWidth={isDragging ? 2 : 0}
                            strokeDasharray={isDragging ? '4 2' : '0'}
                            style={{ cursor: canEdit ? 'grab' : 'default' }}
                            onMouseDown={(e) => handleDragStart(e, item)}
                          />
                          {/* Progress fill */}
                          {pct > 0 && (
                            <rect
                              x={x} y={y} width={Math.max((w * pct / 100), 4)} height={h} rx={4}
                              fill={item.color || '#94a3b8'} opacity={isDragging ? 0.6 : 0.55}
                              style={{ pointerEvents: 'none' }}
                            />
                          )}
                          {/* Label on bar */}
                          <text
                            x={x + 6} y={y + h / 2 + 1} fontSize={11} fontWeight={500}
                            fill="currentColor" className="text-foreground"
                            dominantBaseline="middle" style={{ pointerEvents: 'none' }}
                          >
                            {w > 80 ? `${item.name}${pct > 0 && pct < 100 ? ` (${pct}%)` : ''}` : ''}
                          </text>
                          {/* Invisible wider hit area for dragging */}
                          <rect
                            x={x} y={y - 2} width={Math.max(w, colWidth)} height={h + 4}
                            fill="transparent"
                            style={{ cursor: canEdit ? 'grab' : 'default' }}
                            onMouseDown={(e) => handleDragStart(e, item)}
                          />
                        </g>
                      );
                    })}

                    {/* Today line */}
                    {todayX !== null && (
                      <line x1={todayX} y1={0} x2={todayX} y2={items.length * ROW_H} stroke="#EF4444" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7} />
                    )}
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Drawer */}
      <AppDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={editItem ? 'Edit Phase' : 'Add Phase'}
        footer={
          <div className="flex items-center gap-2">
            {editItem && canEdit && (
              <Button variant="destructive" size="sm" onClick={handleDelete} disabled={saving} data-testid="delete-phase-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={() => setDrawerOpen(false)}>Cancel</Button>
            {canEdit && (
              <Button size="sm" onClick={handleSave} disabled={saving || !form.name?.trim()} data-testid="save-phase-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editItem ? 'Update' : 'Create'}
              </Button>
            )}
          </div>
        }
      >
        <div className="space-y-4 p-4">
          <div>
            <Label>Name *</Label>
            <Input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Phase name" data-testid="phase-name-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start Date</Label>
              <Input type="date" value={form.start_date || ''} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="phase-start-input" />
            </div>
            <div>
              <Label>End Date</Label>
              <Input type="date" value={form.end_date || ''} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} data-testid="phase-end-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Duration (days)</Label>
              <Input type="number" value={form.duration_days || ''} onChange={e => setForm(f => ({ ...f, duration_days: e.target.value }))} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="phase-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CFG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Dependency */}
          <div>
            <Label>Depends On (Finish-to-Start)</Label>
            <Select value={form.depends_on_id || 'none'} onValueChange={v => setForm(f => ({ ...f, depends_on_id: v === 'none' ? '' : v }))}>
              <SelectTrigger data-testid="phase-depends-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No dependency</SelectItem>
                {depOptions.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.depends_on_id && (
              <p className="text-[10px] text-muted-foreground mt-1">This phase will start after the predecessor ends. Dates cascade automatically.</p>
            )}
          </div>
          {/* Percent Complete */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Progress</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium tabular-nums text-muted-foreground">
                  {form.progress_override ? `${form.percent_complete || 0}%` : form.auto_percent !== null && form.auto_percent !== undefined ? `${form.auto_percent}%` : '0%'}
                </span>
                {form.task_count > 0 && (
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, progress_override: !f.progress_override }))}
                    className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
                      form.progress_override
                        ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700'
                        : 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
                    }`}
                    data-testid="progress-mode-toggle"
                  >
                    {form.progress_override ? 'Manual' : 'Auto'}
                  </button>
                )}
              </div>
            </div>
            {form.progress_override ? (
              <Slider
                value={[form.percent_complete || 0]}
                onValueChange={([v]) => setForm(f => ({ ...f, percent_complete: v }))}
                min={0} max={100} step={5}
                data-testid="phase-progress-slider"
              />
            ) : form.task_count > 0 ? (
              <div className="space-y-1.5">
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${form.auto_percent || 0}%` }} />
                </div>
                <p className="text-[10px] text-muted-foreground">
                  {form.task_done_count}/{form.task_count} linked tasks complete · Switch to Manual to override
                </p>
              </div>
            ) : (
              <>
                <Slider
                  value={[form.percent_complete || 0]}
                  onValueChange={([v]) => setForm(f => ({ ...f, percent_complete: v, progress_override: true }))}
                  min={0} max={100} step={5}
                  data-testid="phase-progress-slider"
                />
                <p className="text-[10px] text-muted-foreground mt-1">No linked tasks — set progress manually</p>
              </>
            )}
          </div>
          <div>
            <Label>Assigned To</Label>
            <Select value={form.assigned_to || 'none'} onValueChange={v => setForm(f => ({ ...f, assigned_to: v === 'none' ? '' : v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {(teamUsers || []).map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Color</Label>
            <div className="flex gap-2 mt-1">
              {DEFAULT_COLORS.map(c => (
                <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                  className={`w-6 h-6 rounded-full border-2 ${form.color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
        </div>
      </AppDrawer>
    </div>
  );
}
